import {useEffect, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {
  Title,
  Subheading,
  TextInput,
  Text,
  Button,
  DataTable,
  IconButton,
} from 'react-native-paper';

import {Fund, useFunds} from './../hooks/useDomainData';
import {useAppSettings} from '../components/AppSettings';
import {FundEditor} from '../components/FormikStoreEditors/FundEditor';
import {
  fetchGuestyAuthInfo,
  forceTokenRefresh,
  saveGuestyAuthInfo,
  useGuestyAuthInfo,
} from '../hooks/useGuestyData';
import {useAlerts} from 'react-native-paper-alerts';
import {useQueryClient} from 'react-query';

function GuestySettings() {
  const Alert = useAlerts();
  const queryClient = useQueryClient();
  let authInfo = useGuestyAuthInfo();
  const [clientId, setClientId] = useState('current id');
  const [clientSecret, setClientSecret] = useState('current secret');
  const funds = useFunds();
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    Fund | undefined
  >();

  const appSettings = useAppSettings();
  const themedStyles = StyleSheet.create({
    outlinedVertically: {
      borderTopWidth: 1,
      borderBottomWidth: 1,
      borderColor: appSettings.currentTheme.colors.primary,
    },
  });

  useEffect(() => {
    setClientId(authInfo?.clientId ?? '');
    setClientSecret(authInfo?.clientSecret ?? '');
  }, [authInfo]);

  const handleEditFirestoreObject = (firestoreObject?: Fund) => {
    console.log('open for edit or new object: ' + firestoreObject?.ref.path);
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };

  const handleSaveGuestyAuthInfo = async () => {
    console.log('Saving Guesty API Access Info');
    try {
      await saveGuestyAuthInfo({
        clientId,
        clientSecret,
        name: 'guesty',
      });
      await queryClient.invalidateQueries('guestyAuthInfo');
    } catch (e) {
      Alert.alert('Error', '' + e, [{text: appSettings.t('OK')}]);
    }
  };

  const handleForceTokenRefresh = async () => {
    console.log('Forcing Guesty token refresh');
    try {
      await forceTokenRefresh();
      await queryClient.invalidateQueries('guestyAuthInfo');
      authInfo = await fetchGuestyAuthInfo();
    } catch (e) {
      Alert.alert('Error', '' + e, [{text: appSettings.t('OK')}]);
    }
  };

  return (
    <>
      <ScrollView persistentScrollbar={true} style={[styles.container]}>
        <View style={styles.horizontalCentered}>
          <Title style={styles.title}>Guesty Integration Settings</Title>
        </View>
        <View style={styles.content}>
          <Subheading style={[styles.title, themedStyles.outlinedVertically]}>
            API Access Settings
          </Subheading>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Text>Client ID: </Text>
            <TextInput
              mode="outlined"
              dense
              editable={true}
              onChangeText={text => setClientId(text)}
              value={clientId ?? ''}
            />
          </View>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Text>Client Secret: </Text>
            <TextInput
              mode="outlined"
              dense
              editable={true}
              onChangeText={text => setClientSecret(text)}
              value={clientSecret ?? ''}
            />
          </View>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Button
              mode="outlined"
              compact
              onPress={() => handleSaveGuestyAuthInfo()}>
              Save API Access Settings
            </Button>
          </View>

          <Subheading style={[styles.title, themedStyles.outlinedVertically]}>
            Current Access Token
          </Subheading>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Text>Token Mint Time: </Text>
            <TextInput
              mode="outlined"
              dense
              editable={false}
              value={
                authInfo && authInfo.mintTime
                  ? new Date(authInfo.mintTime * 1000).toLocaleString()
                  : 'No access token'
              }
            />
          </View>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Text>Token Expire Time: </Text>
            <TextInput
              mode="outlined"
              dense
              editable={false}
              value={`${
                authInfo && authInfo.expireTime
                  ? new Date(authInfo.expireTime * 1000).toLocaleString()
                  : 'No access token'
              }`}
            />
          </View>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Text>Token: </Text>
            <TextInput
              mode="outlined"
              dense
              editable={false}
              value={`${authInfo?.accessToken ?? 'No access token'}`}
            />
          </View>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Button
              mode="outlined"
              compact
              onPress={() => handleForceTokenRefresh()}>
              Force Token Refresh (Guesty limit of 5 a day)
            </Button>
          </View>
        </View>
        <View style={[styles.content]}>
          <Subheading style={[styles.title, themedStyles.outlinedVertically]}>
            Custom Credit Field &lt;--&gt; Fund Mappings
          </Subheading>

          {!funds && (
            <DataTable.Row>
              <DataTable.Header>
                <DataTable.Title>loading...</DataTable.Title>
              </DataTable.Header>
            </DataTable.Row>
          )}
          {funds && (
            <DataTable.Header style={styles.shortHeader}>
              <DataTable.Title style={styles.widerCell}>
                Fund Name
              </DataTable.Title>
              <DataTable.Title style={styles.widerCell}>
                Custom Credit Field
              </DataTable.Title>
              <DataTable.Title style={styles.widerCell}>
                Custom Credit Refund Field
              </DataTable.Title>
              <DataTable.Title style={styles.widerCell}>Edit</DataTable.Title>
            </DataTable.Header>
          )}
          {funds &&
            funds.length > 0 &&
            funds.map(fund => (
              <DataTable.Row key={fund.fundId}>
                <DataTable.Cell style={styles.widerCell}>
                  {fund.fund}
                </DataTable.Cell>
                <DataTable.Cell style={styles.widerCell}>
                  {fund.guestyCreditCustomFieldName ?? 'Error: No Mapping!'}
                </DataTable.Cell>
                <DataTable.Cell style={styles.widerCell}>
                  {fund.guestyCreditRefundCustomFieldName ??
                    'Error: No Mapping!'}
                </DataTable.Cell>
                <DataTable.Cell style={styles.widerCell}>
                  <IconButton
                    icon="pencil"
                    size={18}
                    color={appSettings.currentTheme.colors.primary}
                    onPress={() => handleEditFirestoreObject(fund)}
                  />
                </DataTable.Cell>
              </DataTable.Row>
            ))}
        </View>
      </ScrollView>
      <FundEditor
        dialogVisible={editDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setEditDialogVisible(visible);
          if (!visible) {
            setCurrentFirestoreObject(undefined);
          }
        }}
        fund={currentFirestoreObject}
      />
    </>
  );
}

const styles = StyleSheet.create({
  touchableRow: {
    paddingHorizontal: 5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  widerCell: {
    minWidth: 150,
    width: 100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  borderCell: {
    borderStartWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    position: 'relative',
    padding: 20,
    marginVertical: 20,
  },
  content: {
    paddingHorizontal: 40,
  },
  center: {
    width: '100%',
    alignItems: 'center',
  },
  shortHeader: {
    height: 25,
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 10,
  },
});

export default GuestySettings;
